import { useState, useEffect, useRef } from 'react';
import { X, Smartphone } from 'lucide-react';
import { usePortalAuth } from '../PortalAuthContext';
import { usePortalDestination } from '../PortalDestinationContext';
import { usePortalInstallPrompt } from '../lib/pwa';

/**
 * Friendly "Add DutiLyne to your home screen" card.
 *
 * Appears only after the patient's *first* visit — i.e. they see nothing
 * on their very first session, and on a return visit (or after meaningful
 * engagement within the same session) we surface a polished card with the
 * pharmacy/DutiLyne icon, a warm headline, and a single-tap install CTA.
 *
 * Dismissable per-patient (localStorage) and hides immediately once the
 * browser signals `appinstalled`.
 */
export const PortalInstallBanner = () => {
  const { patient } = usePortalAuth();
  const { pharmacy } = usePortalDestination();
  const { canInstall, install } = usePortalInstallPrompt();

  const pid = patient?.patient_user_id || 'anon';
  const visitKey = `portal-first-visit:${pid}`;
  const dismissKey = `portal-install-dismissed:${pid}`;

  const [ready, setReady] = useState(false);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(dismissKey) === '1');

  // Decide "return visitor?" exactly once per key, from the value present at
  // first render — *before* this component writes its own marker. Reading the
  // decision from a ref makes it immune to React StrictMode's double-invoked
  // effects (which would otherwise re-read the marker we just wrote and show
  // the card on the very first visit).
  const decidedRef = useRef({ key: null, isReturn: false });
  if (decidedRef.current.key !== visitKey) {
    decidedRef.current = { key: visitKey, isReturn: localStorage.getItem(visitKey) != null };
  }
  const isReturnVisitor = decidedRef.current.isReturn;

  /* ── First-visit gate ────────────────────────────────────────────── */
  useEffect(() => {
    if (!isReturnVisitor) {
      // First ever visit — mark timestamp and don't show this session
      localStorage.setItem(visitKey, Date.now().toString());
      return;
    }
    // Return visitor: wait a gentle 2.5 s after mount so the card doesn't
    // flash before the patient has oriented themselves.
    const t = setTimeout(() => setReady(true), 2500);
    return () => clearTimeout(t);
  }, [isReturnVisitor, visitKey]);

  if (!canInstall || !ready || dismissed) return null;

  const dismiss = () => { localStorage.setItem(dismissKey, '1'); setDismissed(true); };
  const onInstall = async () => { await install(); dismiss(); };

  return (
    <div
      className="portal-install-card"
      role="status"
      data-testid="portal-install-card"
    >
      <button
        type="button"
        className="portal-install-card-close"
        aria-label="Dismiss"
        onClick={dismiss}
        data-testid="portal-install-card-dismiss"
      >
        <X size={16} />
      </button>

      <div className="portal-install-card-icon" aria-hidden="true">
        <img
          src={pharmacy?.slug
            ? `${process.env.REACT_APP_BACKEND_URL}/api/portal/manifest/${pharmacy.slug}/icon/192.png?purpose=any&v=${encodeURIComponent(pharmacy.icon_version || '2')}`
            : '/portal-icon-192.png'}
          alt=""
          width={44}
          height={44}
          className="portal-install-card-logo"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <Smartphone size={22} className="portal-install-card-phone" />
      </div>

      <h3 className="portal-install-card-title" data-testid="portal-install-card-title">
        Add DutiLyne to your home screen
      </h3>
      <p className="portal-install-card-desc">
        Get instant access to refills, prescriptions and pharmacy chat — just like a native app.
      </p>

      <button
        type="button"
        className="portal-install-card-cta"
        onClick={onInstall}
        data-testid="portal-install-card-install"
      >
        Install app
      </button>
    </div>
  );
};
